import { useCallback, useRef } from "react";
import { useBreakpoint } from "@/hooks/use-breakpoint";

interface DealRightPanelProps {
  isOpen: boolean;
  widthPercent: number;
  onWidthChange: (percent: number) => void;
  fullscreen?: boolean;
  children: React.ReactNode;
}

export function DealRightPanel({
  isOpen,
  widthPercent,
  onWidthChange,
  fullscreen = false,
  children,
}: DealRightPanelProps) {
  const isDragging = useRef(false);
  // Below lg (phone + tablet) the panel takes the full content area and the
  // left tab content is hidden by deal-detail-page — no split, no drag
  // handle. At lg+ the resizable split-pane behaves as before.
  const isSplitEligible = useBreakpoint().isAtLeast("lg");

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!isDragging.current) return;
        const parent = (e.target as HTMLElement).parentElement;
        if (!parent) return;
        const parentRect = parent.getBoundingClientRect();
        const newPercent =
          ((parentRect.right - moveEvent.clientX) / parentRect.width) * 100;
        // Keep both panes at least a third wide — narrower than that and the
        // tab-content cards collide with their header actions.
        const clamped = Math.min(200 / 3, Math.max(100 / 3, newPercent));
        onWidthChange(clamped);
      };

      const handleMouseUp = () => {
        isDragging.current = false;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [onWidthChange]
  );

  if (!isOpen) return null;

  // At <lg the panel renders full-width with no drag handle and no minWidth
  // (the parent flex container drives it). At lg+ the resizable split-pane
  // is preserved.
  const showDragHandle = !fullscreen && isSplitEligible;
  const panelWidth = fullscreen || !isSplitEligible
    ? "100%"
    : `${widthPercent}%`;
  const panelMinWidth = isSplitEligible ? "380px" : "0px";

  return (
    <>
      {/* Drag handle — hidden in fullscreen and below lg (no neighbour to resize against). */}
      {showDragHandle && (
        <div
          onMouseDown={handleMouseDown}
          className="w-1 shrink-0 cursor-col-resize bg-border hover:bg-primary/30 transition-colors active:bg-primary/50 animate-in fade-in duration-200"
        />
      )}

      {/* Panel */}
      <div
        className="@container/panel flex shrink-0 flex-col overflow-hidden animate-in fade-in slide-in-from-right-4 duration-200 ease-out"
        style={{
          width: panelWidth,
          minWidth: panelMinWidth,
          containerType: "inline-size",
        }}
      >
        {children}
      </div>
    </>
  );
}
